import { Component, EventEmitter, inject, Output, signal, ViewChild } from '@angular/core';
import { DynamicColumn, TableComponent } from '../../../../@shared/components/table-component/table-component';
import { Params } from '../../../../services/utilis/add-params-service';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PaginatorState } from 'primeng/paginator';
import { map, distinctUntilChanged, tap, switchMap } from 'rxjs';
import { PricingPlansService } from '../../../../services/setting/plans/pricing-plans-service';
import { PricingPlans } from '../../../../models/pricing-plans';

@Component({
  selector: 'app-pricing-plans-component',
  imports: [TableComponent],
  templateUrl: './pricing-plans-component.html',
  styleUrl: './pricing-plans-component.scss'
})
export class PricingPlansComponent {

  @ViewChild(TableComponent) table!: TableComponent<PricingPlans>;

  @Output() editPricingPlan = new EventEmitter<PricingPlans>();
  @Output() deletePricingPlan = new EventEmitter<PricingPlans>();

  params = [
    "page",
    "size",
    "search",
    'sortBy',
    'sortDirection',
    'minBasePrice',
    'maxBasePrice',
    'minIncludedKilometers',
    'maxIncludedKilometers',
    'minDuration',
    'maxDuration',
    'durationUnit',
  ]

  modifiedParams: Params = {
    page: "page",
    size: "size",
    sortBy: "sortBy",
    sortDirection: "sortDirection",
    minBasePrice: "minBasePrice",
    maxBasePrice: "maxBasePrice",
    minIncludedKilometers: "minIncludedKilometers",
    maxIncludedKilometers: "maxIncludedKilometers",
    minDuration: "minDuration",
    maxDuration: "maxDuration",
    durationUnit: "durationUnit",
  }

  // Define columns to show in the table
  tableColumns: DynamicColumn<PricingPlans>[] = [
    { header: 'ID', field: 'id', sortable: true, },
    { header: 'Name', field: 'name', sortable: true, },
    {
      header: 'Duration',
      field: 'duration',
      sortable: true,
      isFilterable: true,
      filterType: 'slider',
      min: 0,
      max: 10000,
      step: 100,
    },
    {
      header: 'Duration Unit',
      field: 'durationUnit',
      sortable: true,
      isCapitalize: true,
      isFilterable: true,
      filterType: 'multiselect',
      filterOptions: [
        { label: 'Minute', value: 'MINUTE' },
        { label: 'Hour', value: 'HOUR' },
        { label: 'Day', value: 'DAY' },
        { label: 'Week', value: 'WEEK' },
        { label: 'Month', value: 'MONTH' },
      ],
    },
    {
      header: 'Base price',
      field: 'basePrice',
      sortable: true,
      isFilterable: true,
      filterType: 'slider',
      min: 0,
      max: 10000,
      step: 100,
    },
    { header: 'Discount', field: 'discountPercentage', sortable: true, suffixText: "%", iconPosition: "right" },
    {
      header: 'Included km',
      field: 'includedKilometers',
      sortable: true,
      isFilterable: true,
      filterType: 'slider',
      min: 0,
      max: 1000000,
      step: 1000,
    },
    {
      header: 'Final price',
      field: 'finalPrice',
      sortable: false,
    },
    { header: 'Saved amount', field: 'savedAmount', sortable: false },
    { header: 'Model count', field: 'modelCount', sortable: false },
    { header: 'Points', field: 'points', sortable: true },

    { header: 'Activated', field: 'isActive', isBoolean: true, sortable: false, trueLabel: "Activated", falseLabel: "Deactivated" },
    {
      field: 'actions',
      header: 'Actions',
      sortable: false,
      actions: [
        {
          icon: 'edit-icon.svg',
          buttonClass: 'primary-bg-color',
          tooltip: 'Update',
          command: (pricingPlan) => this.editPricingPlan.emit(pricingPlan),
        },
        {
          icon: 'trash-icon.svg',
          buttonClass: 'danger-bg-color',
          tooltip: 'Delete',
          command: (pricingPlan) => this.deletePricingPlan.emit(pricingPlan),
        },
      ],
    },
  ];

  pricingPlansList = signal<PricingPlans[]>([]);
  totalRecords = signal(0);
  first = 0;
  rows = 20;
  searchControl = new FormControl('');

  private pricingPlansService = inject(PricingPlansService)
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute)

  loadData() {
    this.initializeQueryParamsObservable();
  }

  initializeQueryParamsObservable = () => {
    this.activatedRoute.queryParams.pipe(
      map((params) =>
        Object.fromEntries(
          Object.entries(params)
            .filter(([key]) => this.params.includes(key))
            .map(([key, value]) => [this.modifiedParams[key] || key, value])
        )
      ),

      distinctUntilChanged((previous, current) =>
        JSON.stringify(previous) === JSON.stringify(current)
      ),

      tap((queryParams) => {
        this.searchControl.setValue(queryParams['search'], { emitEvent: false });
        this.rows = queryParams['size'] ? +queryParams['size'] : 20;
      }),

      switchMap((queryParams) => {
        const params = {
          ...queryParams,
          sortBy: queryParams['sortBy'] ?? 'updatedAt',
          sortDirection: queryParams['sortDirection'] ?? 'DESC',
        };
        return this.pricingPlansService.getPricingPlansList(params);
      })
    )
      .subscribe((res) => {
        this.pricingPlansList.set(res.data.content);
        this.totalRecords.set(res.data.totalElements)
        this.first = ((res.data.currentPage + 1) * res.data.pageSize) - res.data.pageSize
      });
  };

  onPageChange = (event: PaginatorState) => {
    const queryparams = {
      page: event.page,
      size: event.rows,
    }
    this.router.navigate([], {
      queryParams: queryparams,
      queryParamsHandling: 'merge'
    })
  }

  resetAll() {
    this.table?.clearAllFilters();
  }
}
